import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { formatINR } from '@/utils/format';
import { getData } from '@/services/FetchNodeServices.js';
import { useWarehouseStore } from '@/stores/warehouseStore.js';
import ExecKPICard from './components/ExecKPICard';
import RevenueTrendChart from './components/RevenueTrendChart';
import ProductionStatusDonut from './components/ProductionStatusDonut';
import TopProductsTable from './components/TopProductsTable';
import QualityScorecard from './components/QualityScorecard';
import InventoryHealthCard from './components/InventoryHealthCard';
import CashFlowCard from './components/CashFlowCard';
import AlertsPanel from './components/AlertsPanel';

type PeriodKey = 'this_month' | 'last_month' | 'this_quarter' | 'custom';

/* ─── Quick Actions ─── */
const QUICK_ACTIONS = [
  { label: '+ Sales Invoice', path: '/sales/invoices/new', icon: 'ri-file-add-line', color: 'bg-emerald-600 text-white hover:bg-emerald-700' },
  { label: '+ Purchase Order', path: '/purchase/orders/new', icon: 'ri-shopping-bag-line', color: 'bg-amber-500 text-white hover:bg-amber-600' },
  { label: '+ Production Order', path: '/manufacturing/production-orders/new', icon: 'ri-settings-line', color: 'bg-[#1e293b] text-white hover:bg-[#334155]' },
  { label: 'Run MRP', path: '/manufacturing/mrp', icon: 'ri-calculator-line', color: 'bg-white border border-[#e2e8f0] text-[#1e293b] hover:bg-slate-50' },
  { label: 'Log Production', path: '/manufacturing/shop-floor', icon: 'ri-time-line', color: 'bg-white border border-[#e2e8f0] text-[#1e293b] hover:bg-slate-50' },
  { label: 'Start Inspection', path: '/manufacturing/qc/incoming', icon: 'ri-search-eye-line', color: 'bg-white border border-[#e2e8f0] text-[#1e293b] hover:bg-slate-50' },
];

interface ExecKPIState {
  totalRevenue: number;
  totalPurchases: number;
  netProfit: number;
  profitMargin: string;
  totalTax: number;
  lowStockCount: number;
  totalItems: number;
  outOfStockCount: number;
  overstockCount: number;
  efficiency: number;
  avgOEE: number;
  qualityRate: number;
  openNCRs: number;
}

export default function ExecutiveDashboardPage() {
  const navigate = useNavigate();
  const { selectedWarehouseId } = useWarehouseStore();
  const [activePeriod, setActivePeriod] = useState<PeriodKey>('this_month');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);

  const [kpis, setKpis] = useState<ExecKPIState>({
    totalRevenue: 0,
    totalPurchases: 0,
    netProfit: 0,
    profitMargin: '0',
    totalTax: 0,
    lowStockCount: 0,
    totalItems: 0,
    outOfStockCount: 0,
    overstockCount: 0,
    efficiency: 85,
    avgOEE: 76,
    qualityRate: 98,
    openNCRs: 0,
  });
  const [trend, setTrend] = useState<Array<{ month: string; revenue?: number; purchases?: number }>>([]);
  const [topProducts, setTopProducts] = useState<Array<{ id?: string; name: string; code?: string; quantity?: number; revenue: number }>>([]);
  const [productionStatus, setProductionStatus] = useState<Array<{ status: string; count: number }>>([]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const res = await getData(`api/v1/dashboard/executive?period=${activePeriod}&warehouseId=${selectedWarehouseId}`);
      if (res?.success && res?.data) {
        if (res.data.kpis) setKpis((prev) => ({ ...prev, ...res.data.kpis }));
        if (res.data.trend) setTrend(res.data.trend);
        if (res.data.topProducts) setTopProducts(res.data.topProducts);
        if (res.data.productionStatus) setProductionStatus(res.data.productionStatus);
      }
    } catch (err) {
      console.error('Failed to load executive dashboard data:', err);
    } finally {
      setIsLoading(false);
      setLastUpdated(new Date());
    }
  };

  useEffect(() => {
    loadData();
  }, [activePeriod, selectedWarehouseId]);

  const PERIOD_LABELS: Record<PeriodKey, string> = {
    this_month: 'This Month',
    last_month: 'Last Month',
    this_quarter: 'This Quarter',
    custom: 'Custom',
  };

  const handleRefresh = () => loadData();

  const businessKPIs = [
    {
      label: 'Total Revenue',
      value: isLoading ? 'Loading...' : formatINR(kpis.totalRevenue),
      subtitle: `${activePeriod.replace('_', ' ')} total`,
      icon: 'ri-money-rupee-circle-line',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      trend: 'up' as const,
    },
    {
      label: 'Total Purchases',
      value: isLoading ? 'Loading...' : formatINR(kpis.totalPurchases),
      subtitle: `${activePeriod.replace('_', ' ')} total`,
      icon: 'ri-store-2-line',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      trend: 'up' as const,
    },
    {
      label: 'Net Profit',
      value: isLoading ? 'Loading...' : formatINR(Math.max(0, kpis.netProfit)),
      subtitle: `${kpis.profitMargin}% profit margin`,
      icon: 'ri-line-chart-line',
      iconBg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      trend: kpis.netProfit >= 0 ? ('up' as const) : ('down' as const),
    },
    {
      label: 'GST / Tax Payable',
      value: isLoading ? 'Loading...' : formatINR(kpis.totalTax),
      subtitle: 'Tax collected in period',
      icon: 'ri-government-line',
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-600',
      trend: 'neutral' as const,
      badge: { text: 'Tax info', color: 'bg-rose-50 text-rose-600 border border-rose-200' },
    },
  ];

  const mfgKPIs = [
    {
      label: 'Production Efficiency',
      value: `${kpis.efficiency}%`,
      subtitle: `${kpis.efficiency >= 80 ? 'Target met' : kpis.efficiency >= 60 ? 'Below target' : 'Critical — action needed'}`,
      icon: 'ri-settings-3-line',
      iconBg: kpis.efficiency >= 80 ? 'bg-emerald-50' : kpis.efficiency >= 60 ? 'bg-amber-50' : 'bg-red-50',
      iconColor: kpis.efficiency >= 80 ? 'text-emerald-600' : kpis.efficiency >= 60 ? 'text-amber-600' : 'text-red-600',
      trend: kpis.efficiency >= 80 ? ('up' as const) : ('down' as const),
    },
    {
      label: 'OEE Average',
      value: `${kpis.avgOEE}%`,
      subtitle: 'Target 85%+',
      icon: 'ri-speed-line',
      iconBg: kpis.avgOEE >= 85 ? 'bg-emerald-50' : 'bg-amber-50',
      iconColor: kpis.avgOEE >= 85 ? 'text-emerald-600' : 'text-amber-600',
      trend: kpis.avgOEE >= 85 ? ('up' as const) : ('down' as const),
    },
    {
      label: 'Quality Pass Rate',
      value: `${kpis.qualityRate}%`,
      subtitle: `Target 95%+`,
      icon: 'ri-verified-badge-line',
      iconBg: kpis.qualityRate >= 95 ? 'bg-emerald-50' : 'bg-amber-50',
      iconColor: kpis.qualityRate >= 95 ? 'text-emerald-600' : 'text-amber-600',
      trend: kpis.qualityRate >= 95 ? ('up' as const) : ('down' as const),
    },
    {
      label: 'Open NCRs',
      value: String(kpis.openNCRs),
      subtitle: kpis.openNCRs === 0 ? 'All resolved' : 'Requires attention',
      icon: 'ri-error-warning-line',
      iconBg: kpis.openNCRs === 0 ? 'bg-emerald-50' : 'bg-red-50',
      iconColor: kpis.openNCRs === 0 ? 'text-emerald-600' : 'text-red-600',
      trend: kpis.openNCRs === 0 ? ('up' as const) : ('down' as const),
      badge: kpis.openNCRs > 0 ? { text: `${kpis.openNCRs} open`, color: 'bg-red-50 text-red-600 border border-red-200' } : undefined,
    },
  ];

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* ── Header ── */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#1e293b]">Executive Dashboard</h2>
            <p className="text-sm text-[#64748b] mt-0.5">Complete business overview — all modules</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(Object.keys(PERIOD_LABELS) as PeriodKey[]).map((p) => (
              <button
                key={p}
                onClick={() => setActivePeriod(p)}
                className={`h-8 px-3 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activePeriod === p
                    ? 'bg-[#1e293b] text-white'
                    : 'bg-white border border-[#e2e8f0] text-[#64748b] hover:text-[#1e293b]'
                }`}
              >
                {PERIOD_LABELS[p]}
              </button>
            ))}
            <div className="flex items-center gap-1 text-xs text-[#94a3b8] px-2">
              <i className="ri-time-line" />
              {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <button
              onClick={handleRefresh}
              className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e2e8f0] bg-white text-sm text-[#1e293b] hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line text-[#64748b]" />
              <span className="text-xs font-medium">Refresh</span>
            </button>
          </div>
        </div>

        {/* ── Section 1: Business KPIs ── */}
        <section>
          <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-3">Business KPIs</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {businessKPIs.map((kpi) => (
              <ExecKPICard key={kpi.label} {...kpi} />
            ))}
          </div>
        </section>

        {/* ── Section 2: Manufacturing KPIs ── */}
        <section>
          <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-3">Manufacturing KPIs</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {mfgKPIs.map((kpi) => (
              <ExecKPICard key={kpi.label} {...kpi} />
            ))}
          </div>
        </section>

        {/* ── Section 3: Revenue Trend Chart ── */}
        <RevenueTrendChart data={trend} />

        {/* ── Section 4: Manufacturing Overview ── */}
        <section>
          <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-3">Manufacturing Overview</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ProductionStatusDonut data={productionStatus} />
            <TopProductsTable data={topProducts} />
          </div>
        </section>

        {/* ── Section 5: Quality + Inventory ── */}
        <section>
          <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-3">Quality &amp; Inventory</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <QualityScorecard
              incomingRate={kpis.qualityRate}
              inProcessRate={kpis.qualityRate}
              finalRate={kpis.qualityRate}
              openNCRs={kpis.openNCRs}
            />
            <InventoryHealthCard
              totalItems={kpis.totalItems}
              lowStockCount={kpis.lowStockCount}
              outOfStockCount={kpis.outOfStockCount}
              overstockCount={kpis.overstockCount}
            />
          </div>
        </section>

        {/* ── Section 6+7: Cash Flow + Alerts ── */}
        <section>
          <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-3">Cash Flow &amp; Alerts</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CashFlowCard totalRevenue={kpis.totalRevenue} totalPurchases={kpis.totalPurchases} />
            <AlertsPanel lowStockCount={kpis.lowStockCount} openNCRs={kpis.openNCRs} />
          </div>
        </section>

        {/* ── Section 8: Quick Actions ── */}
        <section>
          <h3 className="text-xs font-semibold text-[#94a3b8] uppercase tracking-wider mb-3">Quick Actions</h3>
          <div className="flex flex-wrap gap-3">
            {QUICK_ACTIONS.map((a) => (
              <button
                key={a.label}
                onClick={() => navigate(a.path)}
                className={`flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${a.color}`}
              >
                <i className={a.icon} />
                {a.label}
              </button>
            ))}
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
